const THEME_FALLBACKS = {
  dark: {
    textHigh: '#e0e6ec',
    textMid: '#ced3df',
    textLow: '#b1bbcd',
    neutralHigh: '#ced3df',
    neutralMuted: '#b1bbcd',
    strokeLow: '#151c2d',
    strokeMid: '#435370',
    surfaceLow: '#15171e',
    primary: '#33b9f7',
    primaryDark: '#009de6',
    primaryA05: 'rgba(51, 185, 247, 0.05)',
    bot: '#ff8723',
    botA25: 'rgba(255, 135, 35, 0.25)',
    botA50: 'rgba(255, 135, 35, 0.5)',
    warning: '#d42c2c',
    tooltipBg: '#21242e',
    tooltipText: '#f8f9fb',
    categoricalPalette: ['#18C387', '#E100E6', '#9735F4', '#E1D800', '#24DAFF', '#E940EC', '#1AFFAF', '#B168F7', '#4190D9', '#ADA604', '#8CFFD7', '#80B5E5', '#026BCC', '#038021', '#67758F', '#CED3DF'],
    categoricalNeutralPalette: ['#67758F', '#CED3DF', '#F4F7F9'],
    threatCategoryColors: {
      automation: '#ff8723',
      knownBotFramework: '#f24c4c',
      browserTampering: '#c3691f',
      clickjacking: '#d42c2c',
      antiDetectBrowser: '#ffc391',
      knownGoodBot: '#ffe0c7',
      other: '#8c98ad',
    },
  },
  light: {
    textHigh: '#151c2d',
    textMid: '#2c384e',
    textLow: '#435370',
    neutralHigh: '#2c384e',
    neutralMuted: '#67758f',
    strokeLow: '#d8dbe0',
    strokeMid: '#d8dbe0',
    surfaceLow: '#e6ebf0',
    primary: '#33b9f7',
    primaryDark: '#009de6',
    primaryA05: 'rgba(51, 185, 247, 0.05)',
    bot: '#ff8723',
    botA25: 'rgba(255, 135, 35, 0.25)',
    botA50: 'rgba(255, 135, 35, 0.5)',
    warning: '#d42c2c',
    tooltipBg: '#e6ebf0',
    tooltipText: '#151c2d',
    categoricalPalette: ['#158760', '#AD04B1', '#752CBB', '#E1D800', '#24DAFF', '#79087C', '#4190D9', '#42A059', '#9735F4', '#0355A3', '#81BF90', '#B168F7', '#06641D', '#797409', '#67758F', '#CED3DF'],
    categoricalNeutralPalette: ['#67758F', '#B1BBCD', '#CED3DF'],
    threatCategoryColors: {
      automation: '#ff8723',
      knownBotFramework: '#f24c4c',
      browserTampering: '#c3691f',
      clickjacking: '#d42c2c',
      antiDetectBrowser: '#ffc391',
      knownGoodBot: '#ffe0c7',
      other: '#8c98ad',
    },
  },
};

const getThemeMode = (modeOrIsDark = null) => {
  if (typeof modeOrIsDark === 'boolean') {
    return modeOrIsDark ? 'dark' : 'light';
  }
  if (modeOrIsDark === 'dark' || modeOrIsDark === 'light') {
    return modeOrIsDark;
  }
  if (typeof document === 'undefined') return 'dark';
  const root = document.documentElement;
  if (root.classList.contains('light-theme')) return 'light';
  return 'dark';
};

const readThemeToken = (styles, name, fallback) => {
  const value = styles?.getPropertyValue(name).trim();
  return value || fallback;
};

export const getChartTheme = (modeOrIsDark = null) => {
  const fallback = THEME_FALLBACKS[getThemeMode(modeOrIsDark)];

  if (typeof document === 'undefined') {
    return fallback;
  }

  const docStyle = getComputedStyle(document.documentElement);

  return {
    textHigh: readThemeToken(docStyle, '--text-contrast-high', fallback.textHigh),
    textMid: readThemeToken(docStyle, '--text-contrast-mid', fallback.textMid),
    textLow: readThemeToken(docStyle, '--text-contrast-low', fallback.textLow),
    strokeLow: readThemeToken(docStyle, '--stroke-contrast-low-mid', fallback.strokeLow),
    strokeMid: readThemeToken(docStyle, '--stroke-contrast-mid-high', fallback.strokeMid),
    surfaceLow: readThemeToken(docStyle, '--surface-contrast-low', fallback.surfaceLow),
    primary: readThemeToken(docStyle, '--primary', fallback.primary),
    primaryDark: readThemeToken(docStyle, '--primary-dark', fallback.primaryDark),
    primaryA05: readThemeToken(docStyle, '--primary-a05', fallback.primaryA05),
    bot: readThemeToken(docStyle, '--bot', fallback.bot),
    botA25: readThemeToken(docStyle, '--bot-a25', fallback.botA25),
    botA50: readThemeToken(docStyle, '--bot-a50', fallback.botA50),
    neutralHigh: readThemeToken(docStyle, '--text-contrast-mid', fallback.neutralHigh),
    neutralMuted: readThemeToken(docStyle, '--text-contrast-low', fallback.neutralMuted),
    warning: readThemeToken(docStyle, '--warning', fallback.warning),
    tooltipBg: readThemeToken(docStyle, '--surface-contrast-mid', fallback.tooltipBg),
    tooltipText: readThemeToken(docStyle, '--text-contrast-high', fallback.tooltipText),
    categoricalPalette: [
      readThemeToken(docStyle, '--chart-series-1', fallback.categoricalPalette[0]),
      readThemeToken(docStyle, '--chart-series-2', fallback.categoricalPalette[1]),
      readThemeToken(docStyle, '--chart-series-3', fallback.categoricalPalette[2]),
      readThemeToken(docStyle, '--chart-series-4', fallback.categoricalPalette[3]),
      readThemeToken(docStyle, '--chart-series-5', fallback.categoricalPalette[4]),
      readThemeToken(docStyle, '--chart-series-6', fallback.categoricalPalette[5]),
      readThemeToken(docStyle, '--chart-series-7', fallback.categoricalPalette[6]),
      readThemeToken(docStyle, '--chart-series-8', fallback.categoricalPalette[7]),
      readThemeToken(docStyle, '--chart-series-9', fallback.categoricalPalette[8]),
      readThemeToken(docStyle, '--chart-series-10', fallback.categoricalPalette[9]),
      readThemeToken(docStyle, '--chart-series-11', fallback.categoricalPalette[10]),
      readThemeToken(docStyle, '--chart-series-12', fallback.categoricalPalette[11]),
      readThemeToken(docStyle, '--chart-series-13', fallback.categoricalPalette[12]),
      readThemeToken(docStyle, '--chart-series-14', fallback.categoricalPalette[13]),
      readThemeToken(docStyle, '--chart-series-15', fallback.categoricalPalette[14]),
      readThemeToken(docStyle, '--chart-series-16', fallback.categoricalPalette[15]),
    ],
    categoricalNeutralPalette: [
      readThemeToken(docStyle, '--chart-series-neutral-1', fallback.categoricalNeutralPalette[0]),
      readThemeToken(docStyle, '--chart-series-neutral-2', fallback.categoricalNeutralPalette[1]),
      readThemeToken(docStyle, '--chart-series-neutral-3', fallback.categoricalNeutralPalette[2]),
    ],
    threatCategoryColors: {
      automation: readThemeToken(docStyle, '--threat-category-automation', fallback.threatCategoryColors.automation),
      knownBotFramework: readThemeToken(docStyle, '--threat-category-framework', fallback.threatCategoryColors.knownBotFramework),
      browserTampering: readThemeToken(docStyle, '--threat-category-browser-tampering', fallback.threatCategoryColors.browserTampering),
      clickjacking: readThemeToken(docStyle, '--threat-category-clickjacking', fallback.threatCategoryColors.clickjacking),
      antiDetectBrowser: readThemeToken(docStyle, '--threat-category-anti-detect', fallback.threatCategoryColors.antiDetectBrowser),
      knownGoodBot: readThemeToken(docStyle, '--threat-category-good-bot', fallback.threatCategoryColors.knownGoodBot),
      other: readThemeToken(docStyle, '--threat-category-other', fallback.threatCategoryColors.other),
    },
  };
};

export const getChartSemanticColors = (theme) => ({
  human: theme.primaryDark,
  bot: theme.bot,
  info: theme.textMid,
});

export const colorToRgba = (color, alpha) => {
  if (!color) return color;
  const normalized = String(color).trim();

  const shortHex = normalized.match(/^#([0-9a-f]{3})$/i);
  if (shortHex) {
    const [r, g, b] = shortHex[1].split('').map((x) => parseInt(x + x, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const fullHex = normalized.match(/^#([0-9a-f]{6})$/i);
  if (fullHex) {
    const raw = fullHex[1];
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const rgb = normalized.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const parts = rgb[1].split(',').map((p) => Number.parseFloat(p.trim()));
    if (parts.length >= 3) {
      const [r, g, b] = parts;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }

  return normalized;
};

export const getFadedLineStroke = (chartsNs, color) => new chartsNs.graphic.LinearGradient(0, 0, 1, 0, [
  { offset: 0, color: colorToRgba(color, 0.22) },
  { offset: 0.12, color: colorToRgba(color, 0.95) },
  { offset: 0.88, color: colorToRgba(color, 0.95) },
  { offset: 1, color: colorToRgba(color, 0.22) },
]);

const DEFAULT_VW = typeof window !== 'undefined' ? window.innerWidth : 1024;
const MOBILE_VW = 390;
const DESKTOP_VW = 1200;

const resolveViewportWidth = (viewportOrMobile = DEFAULT_VW) => {
  if (typeof viewportOrMobile === 'number' && Number.isFinite(viewportOrMobile)) {
    return viewportOrMobile;
  }
  if (typeof viewportOrMobile === 'boolean') {
    if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth)) {
      return window.innerWidth;
    }
    return viewportOrMobile ? MOBILE_VW : DESKTOP_VW;
  }
  return DEFAULT_VW;
};

const fluid = (vw, min, max, minVw = 360, maxVw = 1440) => {
  const t = (vw - minVw) / (maxVw - minVw);
  const value = min + (max - min) * t;
  return Math.max(min, Math.min(max, value));
};

const fluidInt = (vw, min, max, minVw = 360, maxVw = 1440) => {
  return Math.round(fluid(vw, min, max, minVw, maxVw));
};

export const getChartTypography = (viewportOrMobile = DEFAULT_VW) => {
  const vw = resolveViewportWidth(viewportOrMobile);
  return {
    axisLabelSize: fluidInt(vw, 12, 14),
    axisNameSize: fluidInt(vw, 13, 16),
    legendSize: fluidInt(vw, 12, 16),
    tooltipSize: fluidInt(vw, 14, 16),
    strongWeight: 600,
  };
};

export const getChartTooltipStyle = (theme, typography, overrides = {}) => {
  const {
    textStyle: textStyleOverrides = {},
    ...tooltipOverrides
  } = overrides;

  return {
    borderWidth: 0,
    backgroundColor: theme.tooltipBg,
    triggerOn: 'mousemove|click',
    textStyle: {
      color: theme.tooltipText,
      fontSize: typography.tooltipSize,
      ...textStyleOverrides,
    },
    ...tooltipOverrides,
  };
};

export const getChartTooltipMarker = (color) => (
  `<span class="chart-label__swatch chart-label__swatch--tooltip" style="background:${color};"></span>`
);

export const getChartGraphicLabel = (theme, viewportOrMobile, text) => {
  const vw = resolveViewportWidth(viewportOrMobile);
  const typography = getChartTypography(vw);

  return {
    type: 'text',
    left: fluidInt(vw, 7, 9),
    top: fluidInt(vw, 4, 6),
    style: {
      text,
      fill: theme.textHigh,
      fontSize: typography.axisNameSize,
      fontWeight: typography.strongWeight,
      padding: [0, 0, fluidInt(vw, 5, 7), 0],
    },
  };
};

export const getAxisChartGrid = (isMobile, options = {}) => {
  const { hasLegend = true, top } = options;
  return {
    left: isMobile ? 4 : 6,
    right: isMobile ? 4 : 6,
    top: top ?? (isMobile ? '14%' : '12%'),
    bottom: hasLegend ? (isMobile ? '24%' : '20%') : (isMobile ? '14%' : '12%'),
    containLabel: true,
  };
};

export const getChartLegendLayout = (isMobile, theme = null, overrides = {}) => ({
  type: 'scroll',
  orient: 'horizontal',
  icon: 'square',
  left: '2%',
  right: '2%',
  bottom: 0,
  itemWidth: 10,
  itemHeight: 10,
  itemGap: isMobile ? 10 : 12,
  pageIconColor: theme?.textHigh || '#cfd8e6',
  pageIconInactiveColor: theme?.textLow || '#7f8ca3',
  pageTextStyle: {
    color: theme?.textMid || '#9ca9be',
    fontSize: isMobile ? 12 : 13,
  },
  pageButtonGap: isMobile ? 4 : 6,
  pageButtonItemGap: isMobile ? 2 : 4,
  ...overrides,
});
